import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ShoppingCart as OrderIcon,
} from '@mui/icons-material';
import SalesOrderService from '../../services/sales-order.service';

const SalesOrderList: React.FC = () => {
  const navigate = useNavigate();
  const [salesOrders, setSalesOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchSalesOrders();
  }, [statusFilter]);

  const fetchSalesOrders = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['customer', 'quotation', 'createdBy'],
        'pagination[pageSize]': 100,
        sort: 'orderDate:desc',
      };

      if (statusFilter !== 'all') {
        params['filters[status][$eq]'] = statusFilter;
      }

      const response = await SalesOrderService.getAll(params);
      setSalesOrders(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching sales orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this sales order?')) {
      try {
        await SalesOrderService.delete(id);
        fetchSalesOrders();
      } catch (error) {
        console.error('Error deleting sales order:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered':
        return 'success';
      case 'shipped':
        return 'info';
      case 'confirmed':
        return 'primary';
      case 'processing':
        return 'secondary';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number) => {
    if (!amount) return 'OMR 0.00';
    return `OMR ${amount.toFixed(2)}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const filteredOrders = salesOrders.filter((order) => {
    const customer = order.attributes?.customer?.data?.attributes;
    const searchLower = searchTerm.toLowerCase();
    return (
      order.attributes?.orderNumber?.toLowerCase().includes(searchLower) ||
      customer?.companyName?.toLowerCase().includes(searchLower) ||
      customer?.contactPerson?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <OrderIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Sales Orders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/sales-orders/create')}
        >
          New Sales Order
        </Button>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            fullWidth
            label="Search..."
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by order number or customer"
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="shipped">Shipped</MenuItem>
            <MenuItem value="delivered">Delivered</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Order #</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Order Date</TableCell>
              <TableCell>Delivery Date</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : filteredOrders.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No sales orders found
                </TableCell>
              </TableRow>
            ) : (
              filteredOrders.map((order) => {
                const attrs = order.attributes;
                const customer = attrs?.customer?.data?.attributes;

                return (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight="medium">
                        {attrs?.orderNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {customer?.companyName || customer?.contactPerson || '-'}
                    </TableCell>
                    <TableCell>{formatDate(attrs?.orderDate)}</TableCell>
                    <TableCell>
                      {formatDate(attrs?.deliveryDate)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold" color="primary">
                        {formatCurrency(attrs?.total)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={attrs?.status || 'pending'}
                        color={getStatusColor(attrs?.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sales-orders/${order.id}`)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/sales-orders/${order.id}/edit`)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(order.id)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SalesOrderList;

